import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../lib/api';
import { registerServiceWorker, subscribeToPush } from '../lib/push';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for Google OAuth session_id in URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      api.exchangeSession(sessionId)
        .then((data) => {
          if (data.waitlisted) {
            // New Google user needs a passcode — store their info and redirect to auth with state
            window.history.replaceState({}, document.title, window.location.pathname);
            window.dispatchEvent(new CustomEvent('google-waitlisted', { detail: data }));
            return;
          }
          if (data.token) {
            localStorage.setItem('auth_token', data.token);
            setUser(data.user);
          }
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (token) {
      api.getMe()
        .then((userData) => setUser(userData))
        .catch(() => {
          localStorage.removeItem('auth_token');
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  // Register service worker once on mount, and auto-subscribe when user is authenticated
  useEffect(() => { registerServiceWorker(); }, []);
  useEffect(() => {
    if (user) {
      // Try silent subscribe; if permission is default, UI can trigger explicit ask
      subscribeToPush({ silent: true }).catch(() => {});
    }
  }, [user]);

  const login = async (email, password) => {
    const data = await api.login({ email, password });
    localStorage.setItem('auth_token', data.token);
    setUser(data.user);
    // Kick off push subscribe (non-blocking, will no-op if permission not granted)
    subscribeToPush({ silent: false }).catch(() => {});
    return data;
  };

  const register = async (email, password, name, phone, passcode) => {
    const data = await api.register({ email, password, name, phone, passcode });
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
      setUser(data.user);
    }
    return data;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('pp_onboarding_dismissed');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      return userData;
    } catch (e) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
